<script setup lang="ts">
import { computed, onMounted, ref, unref, watch } from 'vue';

import BootstrapButtonDropdown from './bootstrap-button-dropdown.vue';
import PeriodFilterBox from './period-filter-box.vue';
import type { FixedPeriodTableType } from './period-filter-control';
import {
    DefaultFixedPeriod, DefaultFixedPeriodTable, NullFixedPeriod, SelectedPeriod,
    TuttiFixedPeriod,
} from './period-filter-control';

const props = defineProps<{
    periodTable?: FixedPeriodTableType;
    initialPeriod?: string;
    allowTutti?: boolean;
    modelValue?: SelectedPeriod;
    currentlySelected?: SelectedPeriod;
    reset?: number;
    customButton?: boolean;
}>();
//eslint-obsolete no-spaced-func
const emit = defineEmits<{
    (event: 'update:modelValue', value: SelectedPeriod | undefined): void;
    (event: 'update:currentlySelected', value: SelectedPeriod | undefined): void;
    (event: 'custom-button-click', value: undefined): void;
    (event: 'dropdown-closed', value: undefined): void;
}>();

const selectedValue = ref(props.modelValue ?? props.currentlySelected);
const highlightPeriod = ref(props.initialPeriod || DefaultFixedPeriod);
const selectedPeriod = ref(unref(highlightPeriod));
const periodTableRef = computed(() => props.periodTable ?? DefaultFixedPeriodTable);

function emitUpdate() {
    const _selectedValue = unref(selectedValue);
    if(!SelectedPeriod.compare(props.modelValue, _selectedValue)) {
        emit('update:modelValue', _selectedValue);
    }
}

function applyFixedPeriod(period: string) {
    selectedValue.value = SelectedPeriod.fromFixedPeriod(undefined, period, -1);
    highlightPeriod.value = period;
    selectedPeriod.value = period;
    emitUpdate();
}

function applyUserPeriod(_emit: boolean) {
    const _selectedValue = unref(selectedValue)?.toMoment();
    highlightPeriod.value = NullFixedPeriod;
    const _selectedPeriod = unref(selectedPeriod);
    if(_selectedPeriod !== NullFixedPeriod) {
        //if user selected the same period it would be selected from menu - highlight it
        const fixedPeriod = SelectedPeriod.fromFixedPeriod2(undefined, _selectedPeriod, -1);
        if((fixedPeriod.from.getTime() === _selectedValue?.from?.getTime())
            && (fixedPeriod.to.getTime() === _selectedValue?.to?.getTime())) {
            highlightPeriod.value = _selectedPeriod;
        }
    }
    if(_emit) {
        emitUpdate();
    }
}

function dropDownClosed() {
    applyUserPeriod(true);
    emit('dropdown-closed', undefined);
}

watch(() => props.modelValue, () => {
    selectedValue.value = props.modelValue;
    applyUserPeriod(false);
});

watch(() => props.reset, () => {
    selectedValue.value = props.modelValue;
    selectedPeriod.value = props.initialPeriod || DefaultFixedPeriod;
    applyUserPeriod(false);
});

watch(() => props.currentlySelected, (val) => {
    const _selectedValue = unref(selectedValue);
    if(val && !SelectedPeriod.compare(val, _selectedValue)) {
        selectedValue.value = SelectedPeriod.cloneObject(val);
        applyUserPeriod(false);
    }
}, { deep: true });

watch(selectedValue, (_selectedValue) => {
    if(!SelectedPeriod.compare(props.currentlySelected, _selectedValue)) {
        emit('update:currentlySelected', _selectedValue);
    }
}, { deep: true });

onMounted(() => {
    // may initialize default
    applyUserPeriod(true);
    emit('update:currentlySelected', unref(selectedValue));
});
</script>

<template>
    <BootstrapButtonDropdown title="Periodo" @dropdown-closed="dropDownClosed">
        <template v-if="props.customButton" #button>
            <button
                type="button"
                class="btn btn-default"
                @click="emit('custom-button-click', undefined)"
            >
                <span class="bi bi-calendar3" :class="(highlightPeriod === DefaultFixedPeriod) ? 'text-danger' : ''" />
                Periodo
            </button>
        </template>
        <template v-if="!props.customButton" #dropdown>
            <span class="bi bi-calendar3" :class="(highlightPeriod === DefaultFixedPeriod) ? 'text-danger' : ''" />
            Periodo
        </template>
        <template v-for="(value, key) in periodTableRef" :key="key">
            <li :class="(key === highlightPeriod) ? 'active' : ''">
                <a class="button-like" @click.prevent="applyFixedPeriod(key)">{{ value.last }}</a>
            </li>
        </template>
        <li v-if="props.allowTutti">
            <a class="button-like" @click.prevent="applyFixedPeriod(TuttiFixedPeriod)">Tutti</a>
        </li>
        <slot name="periodlist" />
        <li role="separator" class="divider" />
        <li>
            <div class="container container-small form-horizontal">
                <slot name="periodbox" />
                <PeriodFilterBox
                    v-model:model-value="selectedValue"
                    v-model:selected-period="selectedPeriod"
                    :period-table="periodTableRef"
                    @update:model-value="applyUserPeriod(false)"
                    @update:selected-period="applyUserPeriod(false)"
                >
                    <template #buttons>
                        <slot name="periodbuttons" />
                        <button type="button" class="btn btn-success" title="Apply" @click="applyUserPeriod(true)">
                            <span class="bi bi-check-lg" />
                        </button>
                    </template>
                </PeriodFilterBox>
            </div>
        </li>
    </BootstrapButtonDropdown>
</template>
